import React from 'react';
import PropTypes from 'prop-types';
import IdTile from './IdTile';
import Pill from './Pill';
import TrailLink from './TrailLink';

/**
 * `.acct-head` — 72px gradient avatar, kicker + status pill, 30px `h1`, an
 * icon+text subline (each item OMITTED entirely when its value is empty —
 * never a dangling icon or an em dash), right-aligned actions.
 */
function DetailHeader({ name, kicker, statusTone, statusLabel, subline, actions, avatar, nameTo }) {
	return (
		<div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-[1fr_auto] tw:items-start tw:gap-5 tw:border-b tw:border-border tw:pb-6'>
			<div className='tw:flex tw:gap-5'>
				{avatar || <IdTile name={name} size={72} tone='gradient' />}
				<div className='tw:flex tw:flex-col tw:gap-1.5'>
					<div className='tw:flex tw:items-center tw:gap-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
						{kicker}
						{statusLabel && <Pill tone={statusTone || 'neutral'} dot>{statusLabel}</Pill>}
					</div>
					<h1 className='tw:m-0 tw:text-[30px] tw:font-bold tw:leading-[1.1] tw:tracking-[-0.02em] tw:text-ink-0'>
						{nameTo ? (
							<TrailLink to={nameTo} className='tw:text-ink-0 tw:no-underline tw:hover:text-accent'>
								{name}
							</TrailLink>
						) : (
							name
						)}
					</h1>
					{subline && subline.length > 0 && (
						<div className='tw:flex tw:flex-wrap tw:gap-[18px] tw:text-[13px] tw:text-ink-2'>
							{subline.map((item) => (
								<span key={item.label} className='tw:inline-flex tw:items-center tw:gap-1.5'>
									{item.icon}
									{item.strong ? <b className='tw:font-medium tw:text-ink-1'>{item.value}</b> : item.value}
								</span>
							))}
						</div>
					)}
				</div>
			</div>
			{actions && <div className='tw:flex tw:shrink-0 tw:flex-wrap tw:gap-2'>{actions}</div>}
		</div>
	);
}

DetailHeader.propTypes = {
	name: PropTypes.string.isRequired,
	kicker: PropTypes.node.isRequired,
	statusTone: PropTypes.oneOf(['ok', 'warn', 'danger', 'neutral', 'accent']),
	statusLabel: PropTypes.node,
	// Build subline by filtering out empty values BEFORE passing them in —
	// this component renders exactly what it's given, never a dangling icon.
	subline: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			icon: PropTypes.node,
			value: PropTypes.node.isRequired,
			strong: PropTypes.bool,
		}),
	),
	actions: PropTypes.node,
	// Override the default gradient initials tile (e.g. Activo fijo's flat
	// tw:bg-cat-4 tile with a layers glyph, per the Activo detail-page spec).
	avatar: PropTypes.node,
	// When set, the name renders as a Link to this route (e.g. a debt's title
	// linking to its debtor account).
	nameTo: PropTypes.string,
};

export default DetailHeader;
